import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../../components/bootstrap/Card';
import Icon from '../../../../components/icon/Icon';
import ModalBeneficiary from '../../../../components/modals/Beneficiaries/ModalRegister';
import ModalBeneficiaryRecharge from '../../../../components/modals/Beneficiaries/ModalRecharge';
import { DefaultContext } from '../../../../contexts/default';
import masks from '../../../../helpers/utils/masks';
import Money from '../../../../helpers/utils/money/money';
import PreAlert from '../../../../helpers/utils/preAlert';
import Page from '../../../../layout/Page/Page';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import { demoPages } from '../../../../menu';
import api from '../../../../services/api';

const statusLabel = {
  pending_activation: 'Aguardando ativação',
  active: 'Ativo',
  blocked: 'Bloqueado',
};

const statusColor = {
  pending_activation: 'warning',
  active: 'success',
  blocked: 'danger',
};

const errorMessage = error => error?.response?.data?.message || 'Não foi possível concluir a operação';

const Beneficiaries = () => {
  const { estabSelected, onShowAlert, onShowQuestion } = useContext(DefaultContext);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);

  const loadBeneficiaries = useCallback(async () => {
    if (!estabSelected) {
      setBeneficiaries([]);
      return;
    }

    try {
      const response = await api.get('routes/beneficiaries', {
        params: { establishmentId: estabSelected },
      });
      setBeneficiaries(response.data);
    } catch (error) {
      onShowAlert(PreAlert.error(errorMessage(error)));
    }
  }, [estabSelected, onShowAlert]);

  useEffect(() => {
    loadBeneficiaries();
  }, [loadBeneficiaries]);

  const openCreate = useCallback(() => {
    setSelected(null);
    setRegisterOpen(true);
  }, []);

  const openEdit = useCallback(row => {
    setSelected(row);
    setRegisterOpen(true);
  }, []);

  const openRecharge = useCallback(row => {
    setSelected(row);
    setRechargeOpen(true);
  }, []);

  const changeStatus = useCallback(row => {
    if (row.cardStatus === 'pending_activation') return;
    const cardStatus = row.cardStatus === 'active' ? 'blocked' : 'active';
    const action = cardStatus === 'blocked' ? 'bloquear' : 'desbloquear';

    onShowQuestion({
      title: `${action === 'bloquear' ? 'Bloquear' : 'Desbloquear'} cartão`,
      message: `Deseja ${action} o cartão de ${row.name}?`,
      onConfirm: async () => {
        try {
          await api.put(`routes/beneficiaries/${row.beneficiaryId}`, {
            establishmentId: estabSelected,
            cardStatus,
          });
          onShowAlert(PreAlert.success(`Cartão ${cardStatus === 'blocked' ? 'bloqueado' : 'desbloqueado'} com sucesso`));
          loadBeneficiaries();
        } catch (error) {
          onShowAlert(PreAlert.error(errorMessage(error)));
        }
      },
    });
  }, [estabSelected, loadBeneficiaries, onShowAlert, onShowQuestion]);

  const columns = useMemo(() => ([
    {
      label: '',
      field: 'icon',
      format: () => <Icon icon='Person' size='3x' />,
      noExport: true,
    },
    { label: 'Nome', field: 'name' },
    {
      label: 'CPF',
      field: 'cpf',
      format: row => masks.cpf(row.cpf),
      formatExport: value => masks.cpf(value),
    },
    { label: 'Email', field: 'email' },
    {
      label: 'Telefone',
      field: 'phone',
      format: row => masks.phone(row.phone),
      formatExport: value => masks.phone(value),
    },
    { label: 'NFC', field: 'idCard' },
    {
      label: 'Saldo',
      field: 'balance',
      format: row => Money.centsToMaskMoney(row.balance),
      formatExport: value => Money.centsToMaskMoney(value),
    },
    {
      label: 'Senha',
      field: 'cardPasswordConfigured',
      format: row => (row.cardPasswordConfigured || row.cardPinConfigured) ? 'Cadastrada' : 'Pendente',
      formatExport: value => value ? 'Cadastrada' : 'Pendente',
    },
    {
      label: 'Status',
      field: 'cardStatus',
      format: row => (
        <Button
          isLink
          color={statusColor[row.cardStatus] || 'secondary'}
          icon='Circle'
          className='text-nowrap'
          onClick={() => changeStatus(row)}>
          {statusLabel[row.cardStatus] || row.cardStatus}
        </Button>
      ),
      formatExport: value => statusLabel[value] || value,
    },
    {
      label: 'Ações',
      field: 'actions',
      format: row => (
        <div className='d-flex gap-2'>
          <Button
            color='light'
            icon='edit'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => openEdit(row)}
          />
          <Button
            color='success'
            icon='AccountBalanceWallet'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => openRecharge(row)}
          />
        </div>
      ),
      noExport: true,
    },
  ]), [changeStatus, openEdit, openRecharge]);

  return (
    <>
      <PageWrapper title={demoPages.pageLayout.subMenu.beneficiaries.text}>
        <SubHeader>
          <SubHeaderLeft>
            <Breadcrumb list={[
              { title: 'Cadastros', to: '/registers' },
              { title: 'Beneficiários', to: '/register/beneficiaries' },
            ]} />
          </SubHeaderLeft>
          <SubHeaderRight>
            <Button
              color='primary'
              icon='plus'
              shadow='sm'
              hoverShadow='sm'
              isDisable={!estabSelected}
              onClick={openCreate}>
              Cadastrar
            </Button>
          </SubHeaderRight>
        </SubHeader>
        <Page container='fluid'>
          <Card>
            <CardBody className='table-responsive p-5'>
              <SelectEstablishment />
              <TableCustom
                rows={beneficiaries}
                columns={columns}
                keyExtractor={item => item.beneficiaryId}
                fileName='Beneficiários'
              />
            </CardBody>
          </Card>
        </Page>
      </PageWrapper>

      <ModalBeneficiary
        open={registerOpen}
        setIsOpen={setRegisterOpen}
        beneficiary={selected}
        establishmentId={estabSelected}
        onSaved={loadBeneficiaries}
      />

      <ModalBeneficiaryRecharge
        open={rechargeOpen}
        setIsOpen={setRechargeOpen}
        beneficiary={selected}
        establishmentId={estabSelected}
        onSaved={loadBeneficiaries}
      />
    </>
  );
};

export default Beneficiaries;
